import styles from './gpt-route-definition.module.scss';
import { useContext, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { useGetRequestNewHook, useMakeRequestHook } from '../requestUtils';
import { ValueFormatterParams } from 'ag-grid-community';
import { Box, Checkbox, CreateHeaders, Divider, FocusTable, HeaderSizeEnum, Icon, List, ListItemButton, ListItemIcon, ListItemText, MultiLineTextField, PrimaryButton, SecondaryButton, SnackBarContext, TextField, textHeader } from '@frontend-workspace/react-shared-lib';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface GPTRouteDefinitionProps {
  definitions: any,
  refresh: () => void
}

export function GPTRouteDefinition(props: GPTRouteDefinitionProps) {
  const makeRequest = useMakeRequestHook();
  const { data, getData } = useGetRequestNewHook(`gpt/routeDescriptions`, []);
  const [description, setDescription] = useState<string>("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [preloadedMessageIds, setPreloadedMessageIds] = useState<string[]>([]);
  const [detailedDescription, setDetailedDescription] = useState<string>("");
  const [url, setUrl] = useState<string>("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const { t } = useTranslation();

  const handleNewRoute = () => {
    const body = {
      url,
      description,
      detailedDescription,
      id: selectedId,
      preloadedMessageIds,
      isBackup: false
    }
    makeRequest(`gpt/routeDescriptions`, 'PUT', body)
      .then(() => {
        getData();
        setSnackbarMessage({ content: t("gptRouteDefinition.updatedRoute"), type: "success" });
      });
  };
  const handleGetBackup = () => {
    makeRequest(`gpt/routeDescriptions/${selectedId}/backup`)
      .then((result) => {
        setSnackbarMessage({ content: t("standard.backupRetrieved"), type: "success" });
        setSelectedId(result.id);
        setUrl(result.url);
        setDescription(result.description);
        setDetailedDescription(result.detailedDescription);
        setPreloadedMessageIds(result.preloadedMessageIds);
      }).catch(ex => {
        setSnackbarMessage({ content: t("standard.noBackupFound"), type: "error" });
      });
  };
  const handleSaveAsBackup = () => {
    const body = {
      url,
      description,
      detailedDescription,
      id: selectedId,
      preloadedMessageIds,
      isBackup: true
    }
    makeRequest(`gpt/routeDescriptions`, 'PUT', body)
      .then(() => {
        setSnackbarMessage({ content: t("gptRouteDefinition.savedAsBackup"), type: "success" });
      });
  }

  const handleNewRouteSelected = () => {
    setSelectedId(null);
    setUrl("");
    setDescription("");
    setDetailedDescription("");
    setPreloadedMessageIds([]);
    setSelectedIndex(0);
  };

  const preloadSelectedRenderer = (params: ValueFormatterParams) => {
    const isSelected = preloadedMessageIds.includes(params.data.id);
    return (
      <Checkbox
        checked={isSelected}
        onChange={(event: any) => {
          if (isSelected) {
            setPreloadedMessageIds(preloadedMessageIds.filter((id) => id !== params.data.id));
          } else {
            setPreloadedMessageIds([...preloadedMessageIds, params.data.id]);
          }
        }}
      />
    );
  };

  return (
    <Row>
      <Col md={4} xl={3}>
        <List>
          <ListItemButton
            selected={selectedIndex === 0}
            onClick={handleNewRouteSelected}>
            <ListItemIcon className='d-none d-xxl-block'>
              <Icon className="material-icons-outlined d-flex align-items-center">post_add</Icon>
            </ListItemIcon>
            {t("gptRouteDefinition.newPage")}
          </ListItemButton>
          {data?.map((route: any, i: number) => {
            i++;
            const handleSelectUrl = () => {
              setSelectedId(route.id);
              setUrl(route.url);
              setDescription(route.description);
              setDetailedDescription(route.detailedDescription);
              setPreloadedMessageIds(route.preloadedMessageIds);
              setSelectedIndex(i);
            }
            return (
              <ListItemButton
                key={i}
                selected={selectedIndex === i}
                onClick={handleSelectUrl}>
                <ListItemIcon className='d-none d-xxl-block'>
                  <Icon className="material-icons-outlined d-flex align-items-center">article</Icon>
                </ListItemIcon>
                <ListItemText>
                  {route.url}
                </ListItemText>
              </ListItemButton>
            );
          })}
        </List>
      </Col>

      <Col md={8} xl={7}>
        <div className='mb-3'>
          {selectedId &&
            <PrimaryButton
              variant='contained'
              onClick={handleGetBackup}
              className='minButtonWidth'>
              {t("gptRouteDefinition.getBackup")}
            </PrimaryButton>
          }
        </div>
        <div className='mb-3'>
          <TextField
            fullWidth
            value={url}
            label="URL"
            onChange={(event: any) => setUrl(event.target.value)}
            placeholder="/admin/gpt"
          />
        </div>
        <div className='mb-3'>
          <MultiLineTextField
            label={t("standard.shortDescription")}
            fullWidth
            rows={10}
            value={description}
            onChange={(event: any) => setDescription(event.target.value)}
            placeholder="Kort beskrivelse så GPT kan finne siden: e.g. 'Content: All the contracts for a given company. Contracts contain licenses
                  Actions: Create and terminate contracts, edit licenses on an existing contract, create or stop lisenses, move contracts to a different company, combine contracts, cancel future changes to contracts/licenses, add users and hours to support contracts'"
          />
        </div>
        <div className='mb-3'>
          <MultiLineTextField
            label={t("gptRouteDefinition.detailedDescription")}
            fullWidth
            rows={30}
            value={detailedDescription}
            onChange={(event: any) => setDetailedDescription(event.target.value)}
            placeholder={String(t("gptRouteDefinition.detailedDescriptionOfWhatIsOnPageSoGptCanAnswerQuestionsAboutPage"))}
          />
        </div>
        <div className='mb-3'>
          <FocusTable
            data={props.definitions}
            headers={CreateHeaders([
              textHeader("functionName", "ID", HeaderSizeEnum.SMALL, { renderer: "agGroupCellRenderer" }),
              textHeader("description", t("standard.description"), HeaderSizeEnum.EXTRA_LARGE),
              textHeader("selected", t("gptRouteDefinition.selected"), HeaderSizeEnum.EXTRA_SMALL, { renderer: preloadSelectedRenderer }),
            ])}
            expandComponent={definitionExpandComponent}
            expandProps={{ preloadedMessagesIds: preloadedMessageIds }}
          />
        </div>
        <div className="d-flex mb-3">
          <PrimaryButton
            variant='contained'
            onClick={handleNewRoute}
            className='minButtonWidth'>
            {t("gptRouteDefinition.saveRoute")}
          </PrimaryButton>
          {selectedId && <Divider orientation="vertical" variant="middle" flexItem className="mx-4" />}
          {selectedId &&
            <SecondaryButton
              variant='contained'
              onClick={handleSaveAsBackup}
              className='minButtonWidth'>
              {t("standard.saveAsBackup")}
            </SecondaryButton>}
        </div>
      </Col>
    </Row>
  );
}
function definitionExpandComponent(props: any) {
  const { t } = useTranslation();
  console.log("test", props.data.content);
  return (
    <Box className="d-flex flex-column py-4 px-2">
      <MultiLineTextField
        label={t("standard.shortDescription")}
        fullWidth
        rows={20}
        value={props.data.content}
      />
    </Box>
  )
}

export default GPTRouteDefinition;
